import React, { useState } from "react";
import { ModalProps } from "../../../Shared/types/ModalProps";
import { GenericButton } from "../../../Shared/components/GenericButton";
import { GenericTextareaInput } from "../../../Shared/components/GenericInput";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ApprovalService } from "../services/approval.service";
import { CreateApprovalInput } from "../models/CreateApprovalInput";
import { IApproval } from "../models/Approval";
import { useTranslation } from "react-i18next";

type CreateFormProps = ModalProps & {
  approval?: IApproval;
};

export const RejectApprovalForm: React.FC<CreateFormProps> = ({
  onClose,
  approval,
}) => {
  const {t} = useTranslation()
  const message = t('loading_message');

  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [isLoading, setIsLoading] = useState(false);

  const [reason, setReason] = useState(approval?.reason || "");
  const onReason = (val: string) => setReason(val);

  const isFormValid = reason.trim();

  const createAction = async (e: React.FormEvent) => {
    e.preventDefault();

    const approvalApprovalInput = new CreateApprovalInput();
    if (approval?.id) {
      approvalApprovalInput.id = approval?.id;
    }
    approvalApprovalInput.reason = reason;
    try {
      setIsLoading(true);
      await ApprovalService.cancel(approvalApprovalInput);
      onClose(true);
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg w-full max-w-md p-5">
          <div className="flex justify-between items-center pb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {approval?.id
                ? t('reject_approval')
                : t('update_approval_rejection')}
            </h3>
            <button
              onClick={() => onClose(false)}
              className="text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form className="my-3" onSubmit={createAction}>
            <GenericTextareaInput
              label={t('reason')}
              value={reason}
              onChange={onReason}
              placeholder="Enter rejection reason"
              required={false}
            />
            <div className="mx-5 mt-6">
              <GenericButton type="submit" disabled={!isFormValid}>
                {t('submit')}
              </GenericButton>
            </div>
          </form>
        </div>
      </div>

      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </>
  );
};
